// Updated Projects.jsx with Material UI Table
import { API_BASE_URL } from "../config";
import { useEffect, useState } from "react";

import AddModal from "../components/AddModal";
import ProjectUsersModal from "../components/ProjectUsersModal";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Box,
  TableSortLabel,
  Menu,
  MenuItem,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import {
  OtherHousesOutlinedIcon,
  PeopleAltOutlinedIcon,
  AccountTreeOutlinedIcon,
} from "../access/icons/Icons";

function Projects() {
  const [projects, setProjects] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuProject, setMenuProject] = useState(null);

  const role = localStorage.getItem("role");

  const fetchProjects = async () => {
    const role = localStorage.getItem("role") || "user";
    const endpoint =
      role === "admin"
        ? `${API_BASE_URL}/api/admin/projects`
        : `${API_BASE_URL}/api/user/projects/assigned`;

    try {
      const res = await fetch(endpoint, {
        credentials: "include",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await res.json();
      if (res.ok) setProjects(data);
      else console.error("Failed to fetch projects", data);
    } catch (err) {
      console.error("Failed to fetch projects", err);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleEdit = (project) => {
    setEditProject(project);
    setShowSidebar(true);
    handleMenuClose();
  };

  const handleDelete = async (id) => {
    if (!id) return;

    const role = localStorage.getItem("role");

    const res = await fetch(`${API_BASE_URL}/api/${role}/projects/${id}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    if (res.ok) {
      fetchProjects();
    } else {
      const err = await res.json();
      alert(err.error || "Delete failed");
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedProjects = [...projects].sort((a, b) => {
    if (a[orderBy] < b[orderBy]) return order === "asc" ? -1 : 1;
    if (a[orderBy] > b[orderBy]) return order === "asc" ? 1 : -1;
    return 0;
  });

  const handleMenuOpen = (event, project) => {
    setAnchorEl(event.currentTarget);
    setMenuProject(project);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuProject(null);
  };

  return (
    <>
      <Box className="projects-page">
        <Box
          className="header"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" fontWeight={500}>
            Projects
          </Typography>
          {role === "admin" && (
            <div
              className="add-button"
              variant="contained"
              onClick={() => {
                setEditProject(null);
                setShowSidebar(true);
              }}
            >
              <AccountTreeOutlinedIcon />
              Add project
            </div>
          )}
        </Box>

        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <Table className="table">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={orderBy === "name" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "name"}
                    direction={orderBy === "name" ? order : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    Project Name
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "project_number" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "project_number"}
                    direction={orderBy === "project_number" ? order : "asc"}
                    onClick={() => handleSort("project_number")}
                  >
                    Project Number
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "description" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "description"}
                    direction={orderBy === "description" ? order : "asc"}
                    onClick={() => handleSort("description")}
                  >
                    Description
                  </TableSortLabel>
                </TableCell>

                {role === "admin" && (
                  <TableCell
                    sortDirection={orderBy === "userCount" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "userCount"}
                      direction={orderBy === "userCount" ? order : "asc"}
                      onClick={() => handleSort("userCount")}
                    >
                      Assigned Users
                    </TableSortLabel>
                  </TableCell>
                )}

                <TableCell
                  sortDirection={
                    orderBy === "estimated_minutes" ? order : false
                  }
                >
                  <TableSortLabel
                    active={orderBy === "estimated_minutes"}
                    direction={orderBy === "estimated_minutes" ? order : "asc"}
                    onClick={() => handleSort("estimated_minutes")}
                  >
                    Estimated Time
                  </TableSortLabel>
                </TableCell>

                <TableCell
                  sortDirection={orderBy === "time_spent" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "time_spent"}
                    direction={orderBy === "time_spent" ? order : "asc"}
                    onClick={() => handleSort("time_spent")}
                  >
                    Time Spent
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "time_remaining" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "time_remaining"}
                    direction={orderBy === "time_remaining" ? order : "asc"}
                    onClick={() => handleSort("time_remaining")}
                  >
                    Time Remaining
                  </TableSortLabel>
                </TableCell>

                {role === "admin" && (
                  <TableCell style={{ textTransform: "capitalize" }}>
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedProjects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No projects found.
                  </TableCell>
                </TableRow>
              ) : (
                sortedProjects.map((project, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{project.name}</TableCell>
                    <TableCell>{project.project_number}</TableCell>

                    <Tooltip title={project.description}>
                      <TableCell
                        sx={{
                          maxWidth: 350,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {project.description}
                      </TableCell>
                    </Tooltip>

                    {role === "admin" && (
                      <TableCell>
                        <IconButton
                          onClick={() => {
                            setSelectedProjectId(project.id);
                            setShowUsersModal(true);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    )}

                    <TableCell
                      sx={{
                        color:
                          project.estimated_minutes < 30
                            ? "#d32f2f"
                            : project.estimated_minutes < 60
                            ? "#f57c00"
                            : project.estimated_minutes < 120
                            ? "#1976d2"
                            : "#388e3c",
                        fontWeight: "bold",
                      }}
                    >
                      {project.estimated_minutes} (min)
                    </TableCell>

                    <TableCell
                      sx={{
                        color:
                          project.time_spent < 0
                            ? "#d32f2f"
                            : project.time_spent === 0
                            ? "#9e9e9e"
                            : project.time_spent <
                              project.estimated_minutes * 0.3
                            ? "#388e3c"
                            : project.time_spent <
                              project.estimated_minutes * 0.7
                            ? "#f57c00"
                            : "#d32f2f",
                        fontWeight: "bold",
                      }}
                    >
                      {project.time_spent} min
                    </TableCell>
                    <TableCell
                      sx={{
                        color:
                          Number(project.time_remaining) === 0
                            ? "#4caf50" // Zelena za Success
                            : Number(project.time_remaining) < 0 ||
                              Number(project.time_spent) < 0 ||
                              Number(project.time_remaining) >
                                Number(project.estimated_minutes)
                            ? "#d32f2f" // Crvena ako je probijen rok ili realno traje duže
                            : "#fbc02d", // Žuta ako je u toku normalno
                        fontWeight: "bold",
                      }}
                    >
                      {Number(project.time_remaining) === 0
                        ? "Success"
                        : `${project.time_remaining} min`}
                    </TableCell>

                    {role === "admin" && (
                      <TableCell>
                        <IconButton
                          onClick={(event) => handleMenuOpen(event, project)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              if (menuProject) handleEdit(menuProject);
            }}
          >
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => {
              setSelectedProject(menuProject);
              setModalOpen(true);
              handleMenuClose();
            }}
          >
            Delete
          </MenuItem>
        </Menu>

        <ProjectUsersModal
          visible={showUsersModal}
          onClose={() => setShowUsersModal(false)}
          projectId={selectedProjectId}
        />

        <AddModal
          isProject
          editData={editProject}
          open={showSidebar}
          onClose={() => setShowSidebar(false)}
          onUserAdded={() => {
            setShowSidebar(false);
            fetchProjects();
          }}
        />

        <ConfirmDeleteModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onConfirm={async () => {
            await handleDelete(selectedProject?.id);
            setModalOpen(false);
          }}
          itemName="project"
        />
      </Box>
    </>
  );
}

export default Projects;
